import { Timestamp } from '@weblite/ts-action/types'
import dayjs from 'dayjs'
import * as R from 'ramda'
import {
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'

import { MAX_DATE, shortDateFormatter } from '$func/time'
import { CreateInviteLinkInput } from './create-invite-link-input'
import { t } from '$i18n'
import { SelectButton } from '$common/buttons'
import { showInviteLineExpireDatePickerModal } from '$comp/modals/date-picker'

interface InviteLinkTimeInputProps {
  expiresAt?: Date
}

function formatDate(date: Date) {
  return dayjs().isSame(date, 'day')
    ? t('common.today')
    : shortDateFormatter.format(date)
}

interface LinkTimeState {
  time: dayjs.Dayjs
  label: string
}

interface TimeMark extends LinkTimeState {
  value: number
}

export interface CreateInviteLinkTimeInputApiType {
  getValue: () => Timestamp | undefined
}

function generateTimeMarks(state: LinkTimeState) {
  const now = dayjs()

  return R.pipe<
    LinkTimeState[],
    LinkTimeState[],
    LinkTimeState[],
    LinkTimeState[],
    TimeMark[]
  >(
    (...val) => R.append(state)(val),
    R.uniqBy(val => val.label),
    R.sort((a, b) => (a.time.isAfter(b.time) ? 1 : -1)),
    (val: LinkTimeState[]) =>
      val.map((obj, index) => ({ ...obj, value: index })),
  )(
    ...[
      {
        time: now.add(1, 'hour'),
        label: t('inviteLinks.common.oneHour'),
      },
      {
        time: now.add(1, 'day'),
        label: t('inviteLinks.common.oneDay'),
      },
      {
        time: now.add(1, 'week'),
        label: t('inviteLinks.common.oneWeek'),
      },
      {
        time: dayjs(MAX_DATE),
        label: t('inviteLinks.common.unlimited'),
      },
    ],
  )
}

function expireAtToLinkTimeState(
  expiresAt: InviteLinkTimeInputProps['expiresAt'],
) {
  return expiresAt
    ? { time: dayjs(expiresAt), label: formatDate(expiresAt) }
    : { time: dayjs(MAX_DATE), label: t('inviteLinks.common.unlimited') }
}

export const CreateInviteLinkTimeInput = forwardRef(
  function CreateInviteLinkTimeInput(
    { expiresAt }: InviteLinkTimeInputProps,
    ref: ForwardedRef<CreateInviteLinkTimeInputApiType>,
  ) {
    const [state, setState] = useState<LinkTimeState>(
      expireAtToLinkTimeState(expiresAt),
    )

    useEffect(() => {
      setState(expireAtToLinkTimeState(expiresAt))
    }, [expiresAt])

    useImperativeHandle(
      ref,
      () => ({
        getValue: () => {
          const value = state.time.valueOf()
          return value === MAX_DATE.getTime() ? undefined : value
        },
      }),
      [state],
    )

    const marks = generateTimeMarks(state)

    const onSelectButtonClick = async () => {
      await showInviteLineExpireDatePickerModal(
        date => setState({ time: dayjs(date), label: formatDate(date) }),
        expiresAt,
      )
    }

    return (
      <CreateInviteLinkInput
        marks={marks}
        value={R.findIndex(R.propEq('label', state.label), marks)}
        onChange={(value: number) => setState(marks[value])}
        subheader={t('inviteLinks.create.timeLimit.subheader')}
        description={t('inviteLinks.create.timeLimit.description')}
        inputComponent={
          <SelectButton
            placeholder={t('inviteLinks.create.timeLimit.inputPlaceholder')}
            onClick={onSelectButtonClick}
            sx={{ margin: '6px 0 15px' }}
          />
        }
      />
    )
  },
)
